/**
 * Transactions Articles
 */

import type { HelpArticle } from "../types";

export const TRANSACTIONS_ARTICLES: HelpArticle[] = [
  {
    id: "tx-001",
    slug: "transaction-statuses-explained",
    title: "Transaction Statuses Explained",
    description: "Understanding what each status means",
    category: "transactions",
    popular: true,
    order: 1,
    keywords: ["status", "pending", "completed", "processing", "failed"],
    content: `
# Transaction Statuses Explained

Every transaction goes through several stages. Here's what each status means.

## Deposit Statuses

### Detecting
- Transaction seen on blockchain
- Waiting for inclusion in block
- Usually very brief

### Funds Received
- Transaction detected and confirmed on blockchain
- Funds are in our custody
- Waiting for required confirmations

### Confirming / Awaiting Confirmations
- Transaction in block
- Waiting for required confirmations (varies by network)
- Progress shown as X/Y confirmations
- Ethereum: 12 confirmations (~2-5 min)
- Polygon: 128 confirmations (~3-8 min)
- Solana: 32 confirmations (~15-30 sec)

### Converting
- Confirmations complete
- Converting crypto to fiat at current market rate
- Automatic process (no action needed)

### Payment Submitted
- Conversion complete
- Payout instruction sent to payment processor
- On its way to your bank

### Payment Processed / Settled
- Payment processed by bank network
- Funds should appear in your account soon
- Transaction complete

### Completed
- All steps complete
- Fiat received in your bank account
- Transaction finalized

### Failed
- Something went wrong at any stage
- Check error details in transaction view
- Contact support if needed

## Payout Statuses

### Pending
- Queued for processing
- Awaiting compliance check
- Usually very brief

### Processing
- Being prepared for sending
- Compliance approved
- Preparing bank instruction

### Submitted
- Sent to payment processor
- On its way to bank network
- Cannot be cancelled

### In Transit
- In banking network
- Being processed by banks
- Almost there

### Completed
- Delivered to your bank
- Should appear in account
- May take time to reflect

### Returned
- Bank rejected transfer
- Funds returned to balance
- Check error reason

## Status Timeline

### Typical Deposit Flow
\`\`\`
Detecting → Funds Received → Awaiting Confirmations → Converting → 
Payment Submitted → Payment Processed → Settled
   ↓              ↓                    ↓                  ↓
 ~1 min       ~1 min              ~3-10 min          ~1-2 min
                                                           ↓
                                                      ~1-2 min
                                                           ↓
                                                       Done!
\`\`\`

**Note:** The UI may show simplified statuses. Click "View Details" to see the full timeline.

### Typical Payout (ACH)
\`\`\`
Pending → Processing → Submitted → Completed
   ↓          ↓           ↓           ↓
 ~1 min    ~1-4 hours   ~2-3 days    Done!
\`\`\`

## Stuck Transactions

If status doesn't change:
- Deposits: Check blockchain explorer
- Payouts: Contact support after 24h
    `.trim(),
  },
  {
    id: "tx-002",
    slug: "finding-a-transaction",
    title: "Finding a Transaction",
    description: "How to locate specific transactions",
    category: "transactions",
    order: 2,
    keywords: ["find", "search", "locate", "transaction", "history"],
    content: `
# Finding a Transaction

Easily locate any transaction in your history.

## Using Search

### Transaction ID Search
1. Go to Transactions
2. Click search icon
3. Enter transaction ID
4. Results shown instantly

### Date Range Search
1. Go to Transactions
2. Select date filter
3. Choose start and end dates
4. View filtered results

## Filter Options

### By Type
- All transactions
- Deposits only
- Payouts only
- Conversions

### By Status
- All statuses
- Completed
- Pending
- Failed

### By Amount
- Any amount
- Custom range
- Above threshold
- Below threshold

### By Asset
- All assets
- Specific cryptocurrency
- Specific fiat currency

## Transaction Details

Click any transaction to see:
- Full transaction ID
- Timestamps
- Amount details
- Status history
- Blockchain info (deposits)
- Bank info (payouts)

## Export Options

### Download History
1. Go to Transactions
2. Apply desired filters
3. Click "Export"
4. Choose format (CSV, PDF)
5. Download file

### Tax Reporting
- Full year exports available
- Includes all transaction details
- Compatible with tax software

## Tips

Use transaction ID for fastest lookup
Set date range to narrow results
Save common filters
Export regularly for records
    `.trim(),
  },
  {
    id: "tx-003",
    slug: "transaction-history",
    title: "Transaction History",
    description: "Viewing and managing your transaction history",
    category: "transactions",
    order: 3,
    keywords: ["history", "records", "past", "view", "list"],
    content: `
# Transaction History

Your complete record of all Supabank transactions.

## Accessing History

### Web Dashboard
1. Click "Transactions" in sidebar
2. View full transaction list
3. Scroll to load more

### Mobile
1. Tap "Transactions" in bottom nav
2. Pull down to refresh
3. Tap transaction for details

## History Overview

### What's Included
- All deposits
- All payouts
- Conversions
- Fees applied
- Status changes

### Time Period
- Full history retained
- No automatic deletion
- Searchable archive
- Export anytime

## Transaction Cards

Each transaction shows:
- Type icon (deposit/payout)
- Amount and currency
- Date and time
- Current status
- Quick summary

## Detailed View

Click any transaction for:
- Complete breakdown
- All timestamps
- Reference numbers
- Related transactions
- Blockchain links
- Bank references
- Fee breakdown

## Organizing History

### Sort Options
- Newest first (default)
- Oldest first
- Amount (high to low)
- Amount (low to high)

### Group By
- Date
- Type
- Status
- Currency

## Data Retention

- Transaction records: Indefinite
- Blockchain data: Permanent
- Bank references: 7 years
- Detailed logs: 2 years
    `.trim(),
  },
  {
    id: "tx-004",
    slug: "transaction-fees",
    title: "Transaction Fees",
    description: "Understanding fees on transactions",
    category: "transactions",
    order: 4,
    keywords: ["fees", "cost", "charges", "pricing", "commission"],
    content: `
# Transaction Fees

Clear breakdown of all fees applied to transactions.

## Fee Types

### Conversion Fees
- Applied when crypto converts to fiat
- Platform fee: 2.0% of conversion amount
- Bridge fee: ~0.1% of conversion amount
- Total: ~2.1% of conversion amount
- All fees shown before confirmation
- No hidden markups

### Network Fees (Deposits)
- Paid by sender (you)
- Covers blockchain costs
- Varies by network congestion
- Not charged by Supabank

### Payout Fees
| Method | Fee |
|--------|-----|
| ACH | $0 |
| Wire (US) | $15 |
| Wire (Int'l) | $25 |
| SEPA | €0 |
| SPEI | $0 |

## Fee Examples

### Example 1: $1,000 USDC Deposit
- Deposit: $1,000.00
- Platform fee (2%): -$20.00
- Bridge fee (~0.1%): -$1.00
- Payout (ACH): $0
- **Net received: ~$979.00**

### Example 2: 1 ETH Deposit ($3,000)
- Deposit: $3,000.00
- Platform fee (2%): -$60.00
- Bridge fee (~0.1%): -$3.00
- Payout (Wire): -$15.00
- **Net received: ~$2,922.00**

## Viewing Fees

### On Transaction
1. Open transaction details
2. Scroll to "Fees" section
3. See itemized breakdown

### In Summary
- Monthly fee summary in Settings
- Total fees this month
- Comparison to previous

## Fee Transparency

All fees are:
- Shown before confirmation
- Itemized in transaction
- Included in statements
- Never hidden

## Volume Discounts

High-volume users may qualify for:
- Reduced conversion fees
- Waived payout fees
- Custom pricing
- Contact sales for details
    `.trim(),
  },
  {
    id: "tx-005",
    slug: "refunds-policy",
    title: "Refunds Policy",
    description: "How refunds work at Supabank",
    category: "transactions",
    order: 5,
    keywords: ["refund", "return", "cancel", "reverse", "policy"],
    content: `
# Refunds Policy

Understanding when and how refunds are processed.

## Crypto Deposits

### Can Deposits Be Refunded?
**Generally, no.** Crypto transactions are:
- Irreversible by design
- Automatically converted
- Cannot be "sent back"

### Exceptions
Refunds may be possible if:
- Deposit not yet converted
- Significant system error
- Case-by-case basis
- Fees may apply

## Fiat Payouts

### Can Payouts Be Refunded?
If payout is returned by bank:
- Funds credited back to balance
- No additional fee
- Can retry with correct details

### Returned Payouts
Common reasons:
- Incorrect bank details
- Account closed
- Bank rejection
- Name mismatch

## Requesting Assistance

### For Deposits
Contact support with:
- Transaction hash
- Amount sent
- Issue description
- Why refund needed

### For Payouts
Usually automatic:
- Failed payouts return automatically
- Check transaction status
- Contact support if not returned in 5 days

## Processing Time

| Type | Timeline |
|------|----------|
| Returned payout | 1-3 business days |
| Deposit issue review | 3-5 business days |
| Special cases | Up to 14 days |

## Important Notes

⚠️ Crypto sent to wrong address cannot be recovered
⚠️ Conversions cannot be reversed
⚠️ Market rate differences not refundable
⚠️ Network fees are non-refundable
    `.trim(),
  },
  {
    id: "tx-006",
    slug: "dispute-resolution",
    title: "Dispute Resolution",
    description: "How to resolve transaction disputes",
    category: "transactions",
    order: 6,
    keywords: ["dispute", "problem", "issue", "resolve", "complaint"],
    content: `
# Dispute Resolution

How to handle transaction issues and disputes.

## Types of Disputes

### Missing Deposit
- Sent but not received
- Incorrect amount credited
- Delayed beyond expected

### Payout Issues
- Not received at bank
- Wrong amount
- Sent to wrong account

### Conversion Disputes
- Rate different than expected
- Fee disagreement
- Calculation error

## Dispute Process

### Step 1: Gather Information
- Transaction ID
- Blockchain transaction hash
- Screenshots of issue
- Bank statements (if applicable)

### Step 2: Contact Support
Email support@supabank.xyz with:
- Subject: "Dispute - [Transaction ID]"
- Clear description
- All relevant documentation

### Step 3: Investigation
- We review within 24 hours
- May request additional info
- Check all systems and logs

### Step 4: Resolution
- Findings communicated
- Action taken if warranted
- Explanation provided

## Timeline

| Stage | Duration |
|-------|----------|
| Initial review | 24 hours |
| Investigation | 3-5 business days |
| Resolution | Within 14 days |
| Appeals | Additional 14 days |

## What We Investigate

- Blockchain records
- Internal transaction logs
- Bank communication
- System timestamps
- Rate snapshots

## Appeals

If you disagree with resolution:
1. Reply to dispute email
2. Provide new information
3. Escalated to senior review
4. Final decision provided

## Prevention

Verify all details before sending
Use correct networks
Keep records of transactions
Report issues promptly
    `.trim(),
  },
];
